/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { ContextMenuContainer } from "containers/contextMenu/ContextMenuContainer";
import React, { Component } from "react";
import ContextMenuCanvasCss from "./css/ContextMenuCanvas.css";
import { ARROW_DOWN, ARROW_UP, ENTER, ESCAPE, SPACE } from "dc-preview-utils/src/Constants/key-codes";
import { mouseButtons } from "dc-preview-utils/src/Utils/EventHandlingUtils";
import { isEdge, isIE } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { overlayEvents } from "overlays/OverlayEvents";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { logErrorBoundaryInfo } from "logging/logToServer";

class ContextMenuCanvas extends Component {
  componentWillUnmount() {
    this._removeKeydownCaptureHandler && this._removeKeydownCaptureHandler();
    this._removeKeydownHandler && this._removeKeydownHandler();
    this._removeKeyupHandler && this._removeKeyupHandler();
    this._removeClickHandler && this._removeClickHandler();
  }

  componentDidMount() {
    if (this.props.shouldEnableContextMenu === true) {
      this._removeKeydownCaptureHandler = addEventListener(
        this.elementHandle,
        "keydown",
        this.keyDownCaptureHandler.bind(this),
        true
      );
      this._removeKeydownHandler = addEventListener(window, "keydown", this.keyDownHandler.bind(this));
      this._removeKeyupHandler = addEventListener(window, "keyup", this.keyUpHandler.bind(this));
      this._removeClickHandler = addEventListener(window, "click", this.onClick.bind(this));
      overlayEvents.on("pagesViewScrolled", evt => {
        this.onScroll(evt);
      });
    }
  }

  onClick(event) {
    if (event.button !== mouseButtons.RIGHT) {
      this.hideContextMenu();
    }
  }

  keyDownCaptureHandler(eventObj) {
    // For extending selection, prevent navigation inside spectrum menu by stopping propagation in capture phase,
    // Dispatch the same event on handle so that selection handler can handle the event in bubble phase.
    const arrowKeyCodes = [ARROW_UP, ARROW_DOWN];
    if (arrowKeyCodes.includes(eventObj.keyCode) && eventObj.shiftKey) {
      // Do nothing if this is the dispatched Keydown event
      if (eventObj.eventPhase === Event.AT_TARGET) return;
      eventObj.stopPropagation();
      this.elementHandle.dispatchEvent(
        new KeyboardEvent("keydown", {
          keyCode: eventObj.keyCode,
          key: eventObj.key,
          shiftKey: true,
          bubbles: true
        })
      );
    }
  }

  keyDownHandler(eventObj) {
    // If menuItem.shouldStayOpen is true, then keyDown on ENTER key should not reach here
    // If it does, close the menu
    const keyCodesToHideMenu = [ESCAPE, ENTER, SPACE];
    if (keyCodesToHideMenu.includes(eventObj.keyCode)) {
      this.hideContextMenu();
    }
  }

  keyUpHandler(eventObj) {
    // Keyup is needed to capture ESCAPE, since some clients stopPropagation of all keyDown events
    // ENTER key is not checked here as keyup event dows not go through shouldStayOpen check, and event cannot be stopped from reaching here
    if (eventObj.keyCode === ESCAPE) {
      this.hideContextMenu();
    }
  }

  onScroll(event) {
    if (this.props.getContextMenuTriggerType === contextMenuTriggerType.RIGHT_CLICK) {
      this.props.hideContextMenu();
    }
  }

  hideContextMenu() {
    if (this.props.isContextMenuVisible) {
      this.props.hideContextMenu();
    }
  }

  componentDidCatch(error, info) {
    logErrorBoundaryInfo("%O", {
      errorMessage: `${error.stack} ${info.componentStack}`
    });
  }

  render() {
    let activeClass = ContextMenuCanvasCss.Inactive;
    let contextMenu = null;
    if (this.props.isContextMenuVisible) {
      activeClass = ContextMenuCanvasCss.Active;
      contextMenu = <ContextMenuContainer />;
    }
    let className = "";
    if (isEdge() || isIE()) {
      className = ContextMenuCanvasCss.ContextMenuEdgeIE;
    } else {
      className = ContextMenuCanvasCss.ContextMenuCanvas;
    }
    return (
      <div className={`${className} ${activeClass}`} ref={handle => (this.elementHandle = handle)}>
        {contextMenu}
      </div>
    );
  }
}

export { ContextMenuCanvas };
